import { Calendar, Bot, Settings, HelpCircle, Camera, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { BotPersona } from './types'
import { normalizeToVertical, getVerticalProfile } from '@/lib/verticalUI'

interface Step3BotConfigSimplifiedProps {
  aiEnabled: boolean
  botName: string
  avatar: string
  persona: BotPersona
  welcomeMessage: string
  typingSimulation: boolean
  businessType?: string | null
  commercialPolicyConfig?: Record<string, unknown> | null
  checkoutConfig?: Record<string, unknown> | null
  reengagementEnabled?: boolean
  onReengagementEnabledChange?: (value: boolean) => void
  onCommercialPolicyChange?: (policy: Record<string, unknown>) => void
  onCheckoutConfigChange?: (config: Record<string, unknown>) => void
  onAiEnabledChange: (value: boolean) => void
  onBotNameChange: (value: string) => void
  onAvatarChange: (value: string) => void
  onPersonaChange: (value: BotPersona) => void
  onWelcomeMessageChange: (value: string) => void
  onTypingSimulationChange: (value: boolean) => void
  onBack: () => void
  onNext: () => void
}

const AVATAR_COLORS = [
  { name: 'Verde WhatsApp', value: '#25D366' },
  { name: 'Azul', value: '#0084FF' },
  { name: 'Púrpura', value: '#7B68EE' },
  { name: 'Naranja', value: '#FF6B35' },
  { name: 'Rosa', value: '#FF69B4' },
  { name: 'Amarillo', value: '#FFD700' },
]

const PERSONAS = [
  { id: 'Friendly' as const, label: 'Asesor Consultivo ⭐', icon: '🤝', desc: 'Pregunta necesidades y recomienda' },
  { id: 'Professional' as const, label: 'Profesional', icon: '💼', desc: 'Formal y corporativo' },
  { id: 'Tech' as const, label: 'Ágil y Directo', icon: '⚡', desc: 'Respuestas cortas de cierre' },
]

export default function Step3BotConfigSimplified({
  aiEnabled,
  botName,
  avatar,
  persona,
  welcomeMessage,
  typingSimulation,
  businessType,
  reengagementEnabled = false,
  onReengagementEnabledChange,
  onAiEnabledChange,
  onBotNameChange,
  onAvatarChange,
  onPersonaChange,
  onWelcomeMessageChange,
  onTypingSimulationChange,
  onBack,
  onNext,
}: Step3BotConfigSimplifiedProps) {
  const normalizedVertical = normalizeToVertical(businessType)
  const profile = getVerticalProfile(normalizedVertical)
  const isAppointment = !profile.operational.hasFulfillment

  const handleNext = () => {
    if (!botName.trim()) {
      return
    }
    onNext()
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Toggle IA Habilitada - Destacado */}
      <div className="p-5 sm:p-6 bg-gradient-to-r from-[#075E54]/5 via-[#25D366]/5 to-emerald-50 border-2 border-[#075E54]/20 rounded-2xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-[#075E54] to-[#25D366] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
              <Bot className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">Activar Bot con IA</h3>
              <p className="text-sm text-gray-600 mt-1">
                Habilita las respuestas automáticas inteligentes para atender clientes 24/7
              </p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
            <input
              type="checkbox"
              checked={aiEnabled}
              onChange={(e) => onAiEnabledChange(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-16 h-8 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#075E54]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-[#075E54] shadow-inner"></div>
            <span className={cn(
              "ml-3 text-sm font-semibold transition-colors",
              aiEnabled ? "text-[#075E54]" : "text-gray-500"
            )}>
              {aiEnabled ? '✓ Activo' : 'Inactivo'}
            </span>
          </label>
        </div>
      </div>

      {/* Reconocimiento Visual o Asistencia Inteligente según Vertical */}
      <div className="p-3.5 sm:p-4 rounded-2xl bg-sky-50 border border-sky-200/80 flex items-center gap-3">
        <div className="w-8 h-8 rounded-xl bg-sky-600 text-white flex items-center justify-center flex-shrink-0 shadow-sm">
          {isAppointment ? <Calendar className="w-4 h-4" /> : <Camera className="w-4 h-4" />}
        </div>
        <p className="text-xs text-sky-950 leading-relaxed">
          {isAppointment ? (
            <>
              <strong>Atención y Agendamiento 24/7:</strong> Tu asistente podrá resolver dudas, orientar sobre {profile.terminology.itemsPluralName.toLowerCase()} y coordinar solicitudes de citas preliminares para confirmación de tu personal en WhatsApp.
            </>
          ) : (
            <>
              <strong>Reconocimiento Visual Activo:</strong> Tu asistente podrá reconocer fotos de {profile.terminology.itemsPluralName.toLowerCase()} enviadas por tus clientes en WhatsApp y consultar el catálogo automáticamente.
            </>
          )}
        </p>
      </div>

      {/* Grid de 2 columnas en desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Columna 1: Identidad */}
        <div className="space-y-5">
          <div className="flex items-center gap-2 pb-2 border-b">
            <Bot className="w-5 h-5 text-[#075E54]" />
            <h2 className="text-lg font-semibold text-gray-900">Identidad del Bot</h2>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre del Bot
              <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="text"
              value={botName}
              onChange={(e) => onBotNameChange(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:ring-2 focus:ring-[#075E54] focus:border-transparent text-base"
              placeholder="Ej: Asistente Virtual"
            />
            {!botName.trim() && (
              <p className="text-xs text-gray-500 mt-1.5">
                💡 Este nombre aparecerá en las conversaciones con clientes
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Avatar (Color)
            </label>
            <div className="grid grid-cols-6 gap-3">
              {AVATAR_COLORS.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => onAvatarChange(color.value)}
                  className={cn(
                    "w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 transition-all hover:scale-110",
                    avatar === color.value
                      ? "border-[#075E54] ring-2 ring-[#075E54] ring-offset-2 scale-110"
                      : "border-gray-300 hover:border-gray-400"
                  )}
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                />
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Personalidad
            </label>
            <div className="grid grid-cols-3 gap-3">
              {PERSONAS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => onPersonaChange(p.id)}
                  className={cn(
                    "p-3 sm:p-4 rounded-xl border-2 transition-all text-center hover:shadow-md",
                    persona === p.id
                      ? "border-[#075E54] bg-[#075E54]/5 shadow-md"
                      : "border-gray-200 hover:border-gray-300"
                  )}
                >
                  <div className="text-2xl mb-2">{p.icon}</div>
                  <p className="font-medium text-sm text-gray-900">{p.label}</p>
                  <p className="text-xs text-gray-500 mt-1">{p.desc}</p>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Columna 2: Mensajes y Comportamiento */}
        <div className="space-y-5">
          <div className="flex items-center gap-2 pb-2 border-b">
            <Settings className="w-5 h-5 text-[#075E54]" />
            <h2 className="text-lg font-semibold text-gray-900">Comportamiento</h2>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mensaje de Bienvenida
            </label>
            <textarea
              value={welcomeMessage}
              onChange={(e) => onWelcomeMessageChange(e.target.value)}
              rows={4}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:ring-2 focus:ring-[#075E54] focus:border-transparent text-base resize-none"
              placeholder="Escribe el mensaje que recibirán los usuarios al iniciar una conversación..."
            />
            <div className="flex items-center justify-between mt-1.5">
              <p className="text-xs text-gray-500">
                💡 Este mensaje aparecerá como primera respuesta del bot
              </p>
              <span className="text-xs text-gray-400">
                {welcomeMessage.length} caracteres
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">
                Simular "Escribiendo..."
              </label>
              <p className="text-xs text-gray-500">
                Muestra una animación natural antes de enviar
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={typingSimulation}
                onChange={(e) => onTypingSimulationChange(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#075E54]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#075E54]"></div>
            </label>
          </div>

          {/* Preview Card */}
          <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: avatar }}>
                <span className="text-white text-xs font-bold">
                  {botName.charAt(0).toUpperCase() || 'B'}
                </span>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">{botName || 'Mi Bot'}</p>
                <p className="text-xs text-gray-500">Vista previa</p>
              </div>
            </div>
            <div className="bg-white rounded-lg p-3 shadow-sm border">
              <p className="text-sm text-gray-700">
                {welcomeMessage ||
                  (isAppointment
                    ? '¡Hola! 👋 Bienvenido a nuestro centro de atención. ¿En qué podemos ayudarte hoy?'
                    : '¡Hola! 👋 Bienvenido a nuestra tienda. ¿En qué puedo ayudarte hoy?')}
              </p>
            </div>
          </div>

          {/* Tip Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex gap-2">
            <HelpCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900">Consejo</p>
              <p className="text-xs text-blue-700 mt-1">
                Podrás configurar opciones avanzadas como FAQs, horarios de atención y escalamiento a humanos después del onboarding.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toggle Recuperación Automática de Leads (Ventas Perdidas) */}
      <div className="p-4 sm:p-5 bg-gradient-to-r from-amber-500/5 via-amber-50/40 to-white border border-amber-200/80 rounded-2xl">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center flex-shrink-0 mt-0.5">
              <RotateCcw className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="text-sm sm:text-base font-bold text-gray-900">
                  Recuperación Automática de Leads
                </h4>
                <span className="px-2 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-800 rounded-full">
                  Ventas Perdidas
                </span>
              </div>
              <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                El bot enviará un seguimiento amable a los clientes que queden en silencio 20 minutos después de recibir un precio, para rescatar la venta.
              </p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 mt-1">
            <input
              type="checkbox"
              checked={reengagementEnabled}
              onChange={(e) => onReengagementEnabledChange?.(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-amber-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-600 shadow-inner"></div>
          </label>
        </div>
      </div>



      {/* Botones de navegación */}
      <div className="flex justify-between pt-6 border-t">
        <button
          type="button"
          onClick={onBack}
          className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
        >
          ← Atrás
        </button>
        <button
          type="button"
          onClick={handleNext}
          disabled={!botName.trim()}
          className={cn(
            "px-6 py-2.5 rounded-lg font-medium transition-all",
            botName.trim()
              ? "bg-[#075E54] hover:bg-[#064E46] text-white shadow-lg hover:shadow-xl"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          )}
        >
          Continuar →
        </button>
      </div>
    </div>
  )
}
